/**
 * Custom hook for user search functionality in workspace header
 * 
 * This hook manages the user search state and debounced search logic
 * for the invite modal. It handles search input, debouncing, and
 * formatting of search results.
 * 
 * @module useWorkspaceHeaderUserSearch
 */

import { useState, useCallback, useRef } from 'react';
import { createDebounce } from '@tw/tw-component-library';
import {
  searchUsers,
  UserSearchResult,
} from '@/apis/core/user/get/searchUsers';
import {
  formatUserSearchResult,
  MIN_SEARCH_LENGTH,
  SEARCH_DEBOUNCE_DELAY,
  getResetInviteModalState,
} from '../utils/workspaceHeaderUtils';

/**
 * Parameters for the useWorkspaceHeaderUserSearch hook
 */
interface UseWorkspaceHeaderUserSearchParams {
  /** Function to get access token for API calls */
  getAccessToken: () => Promise<string | null>;
}

/**
 * Return type for the useWorkspaceHeaderUserSearch hook
 */
interface UseWorkspaceHeaderUserSearchReturn {
  /** Current search input value */
  memberInput: string;
  /** Setter for search input */
  setMemberInput: (value: string) => void;
  /** Array of formatted option strings for dropdown */
  options: string[];
  /** Whether the dropdown is open */
  isOpen: boolean;
  /** Setter for dropdown open state */
  setIsOpen: (open: boolean) => void;
  /** Whether search is currently loading */
  isLoading: boolean;
  /** Array of last search suggestions (UserSearchResult objects) */
  lastSuggestions: UserSearchResult[];
  /** Handler for search input changes */
  handleInviteSearch: (value: string) => void;
  /** Handler for selecting an option from dropdown */
  handleInviteSelect: (selectedLabel: string | null) => UserSearchResult | null;
  /** Reset all search state to initial values */
  resetSearchState: () => void;
}

/**
 * Custom hook for managing user search in workspace header invite modal
 * 
 * This hook provides:
 * - Debounced user search with minimum character requirement
 * - Formatted search results for dropdown display
 * - State management for search input and results
 * - Non-critical error handling (logs only, no user interruption)
 * 
 * @param params - Hook parameters
 * @returns User search state and handlers
 * 
 * @example
 * ```tsx
 * const {
 *   memberInput,
 *   options,
 *   isOpen,
 *   isLoading,
 *   handleInviteSearch,
 *   handleInviteSelect,
 * } = useWorkspaceHeaderUserSearch({ getAccessToken });
 * ```
 */
export const useWorkspaceHeaderUserSearch = ({
  getAccessToken,
}: UseWorkspaceHeaderUserSearchParams): UseWorkspaceHeaderUserSearchReturn => {
  // ============= State =============
  const [memberInput, setMemberInput] = useState('');
  const [options, setOptions] = useState<string[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [lastSuggestions, setLastSuggestions] = useState<UserSearchResult[]>([]);
  const lastSearchRef = useRef<string>('');

  // ============= Utilities =============

  /**
   * Resets search state to initial values
   */
  const resetSearchState = useCallback(() => {
    const resetState = getResetInviteModalState();
    setMemberInput(resetState.memberInput);
    setOptions(resetState.options);
    setLastSuggestions(resetState.lastSuggestions);
    setIsOpen(resetState.isOpen);
    setIsLoading(false);
    lastSearchRef.current = '';
  }, []);

  /**
   * Debounced user search function
   * 
   * Performs a search after the user stops typing for the debounce delay.
   * Only searches if input is at least MIN_SEARCH_LENGTH characters.
   * Non-critical errors are logged but don't interrupt user experience.
   */
  const debouncedUserSearch = useCallback(
    (value: string) => {
      const debouncedFn = createDebounce(async (searchValue: string) => {
        // Skip if this search was already performed
        if (lastSearchRef.current === searchValue) return;
        lastSearchRef.current = searchValue;

        // Clear results if search is too short
        if (!searchValue.trim() || searchValue.trim().length < MIN_SEARCH_LENGTH) {
          setIsOpen(false);
          setIsLoading(false);
          setOptions([]);
          setLastSuggestions([]);
          return;
        }

        try {
          setIsLoading(true);
          const response = await searchUsers({
            getAccessToken,
            searchValue: searchValue.trim(),
          });
          const users = (response as UserSearchResult[]) || [];
          setLastSuggestions(users);
          
          // Format users for dropdown display
          setOptions(users.map(formatUserSearchResult));
          setIsOpen(users.length > 0);
        } catch (err) {
          // Non-critical error: Log only - don't interrupt user experience
          console.error('Error searching users:', err);
          setOptions([]);
          setLastSuggestions([]);
          setIsOpen(false);
        } finally {
          setIsLoading(false);
        }
      }, SEARCH_DEBOUNCE_DELAY);

      debouncedFn(value);
    },
    [getAccessToken]
  );

  // ============= Handlers =============

  /**
   * Handles search input changes
   * 
   * Updates the input value and triggers debounced search if input is valid.
   * Clears results immediately if input is empty.
   */
  const handleInviteSearch = useCallback(
    (value: string) => {
      setMemberInput(value);

      if (!value.trim()) {
        setIsOpen(false);
        setIsLoading(false);
        setOptions([]);
        setLastSuggestions([]);
        return;
      }

      debouncedUserSearch(value);
    },
    [debouncedUserSearch]
  );

  /**
   * Handles selection of a user from the dropdown
   * 
   * Finds the matching UserSearchResult from the formatted label and returns it.
   * Clears search state after selection.
   * 
   * @param selectedLabel - The formatted label string selected from dropdown
   * @returns The matching UserSearchResult or null if not found
   */
  const handleInviteSelect = useCallback(
    (selectedLabel: string | null): UserSearchResult | null => {
      if (!selectedLabel) {
        setMemberInput('');
        setIsOpen(false);
        return null;
      }

      // Find the matching suggestion by comparing formatted labels
      const match = lastSuggestions.find((suggestion) => {
        const formattedLabel = formatUserSearchResult(suggestion);
        return formattedLabel === selectedLabel;
      });

      if (match) {
        // Clear search state after selection
        setMemberInput('');
        setIsOpen(false);
        setOptions([]);
        setLastSuggestions([]);
        return match;
      }

      return null;
    },
    [lastSuggestions]
  );

  return {
    memberInput,
    setMemberInput,
    options,
    isOpen,
    setIsOpen,
    isLoading,
    lastSuggestions,
    handleInviteSearch,
    handleInviteSelect,
    resetSearchState,
  };
};
